import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
    Node,
    Edge,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    BackgroundVariant,
    NodeProps,
    Handle,
    Position,
    MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Typography } from 'antd';
import {
    GitPullRequest,
    Box,
    Layers,
    Trash2,
    AlertCircle,
    Pause,
    Tag as TagIcon,
} from 'lucide-react';
import type { PreviewConfigFormState } from '../../../types/preview-environments';

const { Text } = Typography;

// ─── Shared Primitives ────────────────────────────────────────────────────────

function NodeShell({
    children,
    color,
    isConfigured,
    isSelected,
    onClick,
    noSource,
    noTarget,
    maxWidth,
}: {
    children: React.ReactNode;
    color: { border: string; selectedBorder: string; shadow: string };
    isConfigured: boolean;
    isSelected?: boolean;
    onClick?: () => void;
    noSource?: boolean;
    noTarget?: boolean;
    maxWidth?: number;
}) {
    const border = isSelected
        ? `2px solid ${color.selectedBorder}`
        : isConfigured
        ? `2px solid ${color.border}`
        : '2px dashed #d1d5db';

    const shadow = isSelected
        ? `0 6px 20px ${color.shadow}`
        : isConfigured
        ? `0 4px 14px ${color.shadow}`
        : '0 2px 8px rgba(0,0,0,0.06)';

    return (
        <div
            onClick={onClick}
            style={{
                padding: '20px 24px',
                borderRadius: 14,
                background: isConfigured ? '#fff' : '#fafafa',
                border,
                boxShadow: shadow,
                minWidth: 280,
                maxWidth: maxWidth,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s',
                position: 'relative',
            }}
            onMouseEnter={onClick ? (e) => {
                e.currentTarget.style.borderColor = color.selectedBorder;
                e.currentTarget.style.boxShadow = `0 6px 20px ${color.shadow}`;
                e.currentTarget.style.transform = 'translateY(-1px)';
            } : undefined}
            onMouseLeave={onClick ? (e) => {
                e.currentTarget.style.borderColor = isSelected
                    ? color.selectedBorder
                    : isConfigured ? color.border : '#d1d5db';
                e.currentTarget.style.borderStyle = isConfigured || isSelected ? 'solid' : 'dashed';
                e.currentTarget.style.boxShadow = isSelected
                    ? `0 6px 20px ${color.shadow}`
                    : isConfigured ? `0 4px 14px ${color.shadow}` : '0 2px 8px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
            } : undefined}
        >
            {!noTarget && (
                <Handle type="target" position={Position.Left} style={{ background: '#9ca3af', width: 10, height: 10, border: '2px solid #fff' }} />
            )}
            {children}
            {!noSource && (
                <Handle type="source" position={Position.Right} style={{ background: '#9ca3af', width: 10, height: 10, border: '2px solid #fff' }} />
            )}
        </div>
    );
}

function NodeHeader({ icon, title, subtitle, badge }: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    badge?: React.ReactNode;
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {icon}
            <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 15, display: 'block', color: '#111827', lineHeight: '20px' }}>
                    {title}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {subtitle}
                </Text>
            </div>
            {badge}
        </div>
    );
}

function NodeIcon({ bg, children }: { bg: string; children: React.ReactNode }) {
    return (
        <div style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}>
            {children}
        </div>
    );
}

function DataRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
    return (
        <div style={{
            padding: '8px 12px',
            background: muted ? '#f3f4f6' : '#f9fafb',
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
        }}>
            <Text style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {label}
            </Text>
            <Text style={{
                fontSize: 13,
                color: muted ? '#9ca3af' : '#374151',
                fontWeight: 500,
            }}>
                {value}
            </Text>
        </div>
    );
}

function UnconfiguredBadge() {
    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 8px',
            borderRadius: 6,
            background: '#fef3c7',
            border: '1px solid #fde68a',
        }}>
            <AlertCircle size={11} style={{ color: '#d97706' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#d97706' }}>Setup required</span>
        </div>
    );
}

// ─── Node Components ──────────────────────────────────────────────────────────

function TriggerEventBadges({ triggerEvents }: { triggerEvents?: string[] }) {
    const events = triggerEvents && triggerEvents.length > 0 ? triggerEvents : ['PR'];
    return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {events.includes('PR') && (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 4,
                    background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)',
                    fontSize: 11, fontWeight: 600, color: '#3b82f6',
                }}>
                    <GitPullRequest size={10} /> PR
                </span>
            )}
            {events.includes('TAG') && (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 4,
                    background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.25)',
                    fontSize: 11, fontWeight: 600, color: '#ca8a04',
                }}>
                    <TagIcon size={10} /> Tag
                </span>
            )}
        </div>
    );
}

const TriggerNode = React.memo(function TriggerNode({ data, selected }: NodeProps) {
    const isConfigured = !!data.targetBranch;
    const triggerEvents: string[] = data.triggerEvents && data.triggerEvents.length > 0 ? data.triggerEvents : ['PR'];
    const hasPR = triggerEvents.includes('PR');
    const hasTAG = triggerEvents.includes('TAG');
    const color = { border: '#93c5fd', selectedBorder: '#3b82f6', shadow: 'rgba(59, 130, 246, 0.2)' };

    return (
        <NodeShell color={color} isConfigured={isConfigured} isSelected={selected} onClick={data.onClick} noTarget noSource>
            <Handle
                type="source"
                position={Position.Right}
                id="open"
                style={{ top: '35%', background: '#10b981', width: 10, height: 10, border: '2px solid #fff' }}
            />
            <Handle
                type="source"
                position={Position.Right}
                id="close"
                style={{ top: '65%', background: '#ef4444', width: 10, height: 10, border: '2px solid #fff' }}
            />

            <NodeHeader
                icon={<NodeIcon bg="linear-gradient(135deg, #eff6ff, #dbeafe)"><GitPullRequest size={20} style={{ color: '#3b82f6' }} /></NodeIcon>}
                title="Git Event"
                subtitle="Trigger & branch filter"
                badge={
                    !isConfigured ? <UnconfiguredBadge /> :
                    data.enabled === false
                        ? <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 9px',
                            borderRadius: 6,
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                        }}>
                            <Pause size={10} style={{ color: '#9ca3af' }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>Paused</span>
                        </div>
                        : <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 9px',
                            borderRadius: 6,
                            background: 'rgba(59, 130, 246, 0.08)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                        }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6' }}>Trigger</span>
                        </div>
                }
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{
                    padding: '8px 12px',
                    background: '#f9fafb',
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                }}>
                    <Text style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                        Trigger on
                    </Text>
                    <TriggerEventBadges triggerEvents={triggerEvents} />
                </div>
                {hasPR && (
                    <>
                        <DataRow label="Target branch" value={data.targetBranch || 'Not configured'} muted={!data.targetBranch} />
                        <DataRow label="Branch pattern" value={data.branchPattern || 'All branches'} muted={!data.branchPattern} />
                    </>
                )}
                {hasTAG && (
                    <DataRow label="Tag pattern" value={data.tagPattern || 'All tags'} muted={!data.tagPattern} />
                )}
            </div>
        </NodeShell>
    );
});

const CreateNamespaceNode = React.memo(function CreateNamespaceNode({ data, selected }: NodeProps) {
    const isConfigured = !!(data.clusterId && data.namingPattern);
    const color = { border: '#7dd3fc', selectedBorder: '#0ea5e9', shadow: 'rgba(14, 165, 233, 0.2)' };

    return (
        <NodeShell color={color} isConfigured={isConfigured} isSelected={selected} onClick={data.onClick}>
            <NodeHeader
                icon={<NodeIcon bg="linear-gradient(135deg, #ecfeff, #cffafe)"><Box size={20} style={{ color: '#0ea5e9' }} /></NodeIcon>}
                title="Create Namespace"
                subtitle="Provision preview namespace"
                badge={!isConfigured ? <UnconfiguredBadge /> : undefined}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <DataRow label="Cluster" value={data.clusterName || 'No cluster selected'} muted={!data.clusterName} />
                <DataRow label="Namespace pattern" value={data.namingPattern || 'Not configured'} muted={!data.namingPattern} />
                <DataRow label="Max concurrent" value={`${data.maxConcurrentPreviews || 5} previews`} />
            </div>
            <div style={{
                marginTop: 10,
                padding: '6px 10px',
                background: '#ecfdf5',
                borderRadius: 6,
                border: '1px solid #a7f3d0',
            }}>
                <Text style={{ fontSize: 11, color: '#059669', lineHeight: '16px' }}>
                    Creates new namespace or reuses existing for this PR
                </Text>
            </div>
        </NodeShell>
    );
});

const APPS_LIMIT = 5;

const DeployAppsNode = React.memo(function DeployAppsNode({ data, selected }: NodeProps) {
    const [expanded, setExpanded] = useState(false);
    const includedApps = (data.apps || []).filter((a: any) => a.included);
    const isConfigured = includedApps.length > 0;
    const hasMore = includedApps.length > APPS_LIMIT;
    const visibleApps = expanded ? includedApps : includedApps.slice(0, APPS_LIMIT);
    const color = { border: '#fcd34d', selectedBorder: '#f59e0b', shadow: 'rgba(245, 158, 11, 0.2)' };

    return (
        <NodeShell color={color} isConfigured={isConfigured} isSelected={selected} onClick={data.onClick} noSource>
            <NodeHeader
                icon={<NodeIcon bg="linear-gradient(135deg, #fffbeb, #fef3c7)"><Layers size={20} style={{ color: '#f59e0b' }} /></NodeIcon>}
                title="Deploy Apps"
                subtitle="Build & deploy to preview"
                badge={!isConfigured ? <UnconfiguredBadge /> : undefined}
            />
            <div style={{
                padding: '8px 12px',
                background: isConfigured ? '#f9fafb' : '#f3f4f6',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
                <Text style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 4 }}>
                    {isConfigured ? `${includedApps.length} app${includedApps.length !== 1 ? 's' : ''} selected` : 'Apps'}
                </Text>
                {isConfigured ? (
                    <>
                        {visibleApps.map((app: any) => {
                            const varCount = (app.envVarOverrides || []).length;
                            return (
                                <div key={app.appName} style={{
                                    fontSize: 13,
                                    color: '#374151',
                                    fontWeight: 500,
                                    padding: '3px 0',
                                    borderBottom: '1px solid #f3f4f6',
                                    lineHeight: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <span>{app.appName}</span>
                                    {varCount > 0 && (
                                        <span style={{
                                            fontSize: 10,
                                            color: '#6b7280',
                                            background: '#e5e7eb',
                                            borderRadius: 4,
                                            padding: '1px 5px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                            marginLeft: 8,
                                        }}>
                                            {varCount} var{varCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {hasMore && (
                            <button
                                onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
                                style={{
                                    marginTop: 4,
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#f59e0b',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                }}
                            >
                                {expanded ? 'Show less' : `+${includedApps.length - APPS_LIMIT} more`}
                            </button>
                        )}
                    </>
                ) : (
                    <Text style={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>No apps selected</Text>
                )}
            </div>
        </NodeShell>
    );
});

const RemovePreviewNode = React.memo(function RemovePreviewNode({ data, selected }: NodeProps) {
    const color = { border: '#fca5a5', selectedBorder: '#ef4444', shadow: 'rgba(239, 68, 68, 0.2)' };
    const triggerEvents: string[] = data.triggerEvents && data.triggerEvents.length > 0 ? data.triggerEvents : ['PR'];
    const isTagOnly = triggerEvents.length === 1 && triggerEvents[0] === 'TAG';
    const isManual = isTagOnly || !data.autoDestroyOnPRClose;

    return (
        <NodeShell color={color} isConfigured isSelected={selected} onClick={data.onClick} noSource maxWidth={340}>
            <NodeHeader
                icon={<NodeIcon bg="linear-gradient(135deg, #fef2f2, #fee2e2)"><Trash2 size={20} style={{ color: '#ef4444' }} /></NodeIcon>}
                title="Remove Preview"
                subtitle={isTagOnly ? "Manual cleanup" : "Cleanup on PR close"}
                badge={
                    isManual
                        ? <div style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '3px 9px', borderRadius: 6,
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                        }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}>Manual</span>
                        </div>
                        : <div style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '3px 9px', borderRadius: 6,
                            background: 'rgba(239, 68, 68, 0.08)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                        }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>Auto</span>
                        </div>
                }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <DataRow label="Action" value="Delete namespace & all deployed apps" />
                <DataRow
                    label="Mode"
                    value={isTagOnly ? 'Manual removal required' : data.autoDestroyOnPRClose ? 'Automatic on PR close' : 'Manual removal required'}
                    muted={isManual}
                />
            </div>
            {isTagOnly ? (
                <div style={{
                    marginTop: 10,
                    padding: '6px 10px',
                    background: '#fffbeb',
                    borderRadius: 6,
                    border: '1px solid #fde68a',
                }}>
                    <Text style={{ fontSize: 11, color: '#d97706', lineHeight: '16px' }}>
                        Tag-triggered previews have no auto-destroy. Remove previews manually from the Previews tab.
                    </Text>
                </div>
            ) : !data.autoDestroyOnPRClose ? (
                <div style={{
                    marginTop: 10,
                    padding: '6px 10px',
                    background: '#fffbeb',
                    borderRadius: 6,
                    border: '1px solid #fde68a',
                }}>
                    <Text style={{ fontSize: 11, color: '#d97706', lineHeight: '16px' }}>
                        Auto-destroy is disabled. Previews must be removed manually.
                    </Text>
                </div>
            ) : null}
        </NodeShell>
    );
});

// ─── Node Type Registry ───────────────────────────────────────────────────────

const nodeTypes = {
    trigger: TriggerNode,
    createNamespace: CreateNamespaceNode,
    deployApps: DeployAppsNode,
    removePreview: RemovePreviewNode,
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
    formState: PreviewConfigFormState;
    updateFormState: (updates: Partial<PreviewConfigFormState>) => void;
    clusters?: Array<{ id: string; name: string }>;
    onNodeSelect: (nodeId: string | null) => void;
    selectedNodeId: string | null;
}

export default function PreviewWorkflowCanvas({ formState, clusters, onNodeSelect, selectedNodeId }: Props) {
    const selectedCluster = clusters?.find(c => c.id === formState.rootClusterId);

    const handleNodeClick = useCallback((nodeId: string) => {
        onNodeSelect(nodeId);
    }, [onNodeSelect]);

    const initialNodes: Node[] = [
        {
            id: 'trigger',
            type: 'trigger',
            position: { x: 0, y: 160 },
            data: {
                enabled: formState.enabled,
                targetBranch: formState.targetBranch,
                branchPattern: formState.branchPattern,
                triggerEvents: formState.triggerEvents,
                tagPattern: formState.tagPattern,
                onClick: () => handleNodeClick('trigger'),
            },
        },
        {
            id: 'createNamespace',
            type: 'createNamespace',
            position: { x: 480, y: 0 },
            data: {
                clusterId: formState.rootClusterId,
                clusterName: selectedCluster?.name,
                namingPattern: formState.namingPattern,
                maxConcurrentPreviews: formState.maxConcurrentPreviews,
                onClick: () => handleNodeClick('namespace'),
            },
        },
        {
            id: 'deployApps',
            type: 'deployApps',
            position: { x: 920, y: 0 },
            data: {
                apps: formState.apps,
                postCommentToPR: formState.postCommentToPR,
                onClick: () => handleNodeClick('apps'),
            },
        },
        {
            id: 'removePreview',
            type: 'removePreview',
            position: { x: 480, y: 420 },
            data: {
                autoDestroyOnPRClose: formState.autoDestroyOnPRClose,
                triggerEvents: formState.triggerEvents,
                onClick: () => handleNodeClick('cleanup'),
            },
        },
    ];

    const triggerEventsForEdge: string[] = formState.triggerEvents && formState.triggerEvents.length > 0 ? formState.triggerEvents : ['PR'];
    const isTagOnlyEdge = triggerEventsForEdge.length === 1 && triggerEventsForEdge[0] === 'TAG';

    const openEdgeStyle = { stroke: '#10b981', strokeWidth: 2 };
    const closeEdgeStyle = { stroke: isTagOnlyEdge ? '#9ca3af' : '#ef4444', strokeWidth: 2, strokeDasharray: isTagOnlyEdge ? '6 4' : undefined };
    const defaultEdgeStyle = { stroke: '#9ca3af', strokeWidth: 2 };

    const initialEdges: Edge[] = [
        {
            id: 'e-open',
            source: 'trigger',
            sourceHandle: 'open',
            target: 'createNamespace',
            type: 'smoothstep',
            style: openEdgeStyle,
            label: 'Event matched',
            labelStyle: { fontSize: 11, fontWeight: 600, fill: '#059669' },
            labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
            labelBgPadding: [6, 4] as [number, number],
            labelBgBorderRadius: 4,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981', width: 18, height: 18 },
        },
        {
            id: 'e-deploy',
            source: 'createNamespace',
            target: 'deployApps',
            type: 'smoothstep',
            style: defaultEdgeStyle,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#9ca3af', width: 18, height: 18 },
        },
        {
            id: 'e-close',
            source: 'trigger',
            sourceHandle: 'close',
            target: 'removePreview',
            type: 'smoothstep',
            style: closeEdgeStyle,
            label: isTagOnlyEdge ? 'Manual removal' : 'PR closed / merged',
            labelStyle: { fontSize: 11, fontWeight: 600, fill: isTagOnlyEdge ? '#9ca3af' : '#dc2626' },
            labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
            labelBgPadding: [6, 4] as [number, number],
            labelBgBorderRadius: 4,
            markerEnd: { type: MarkerType.ArrowClosed, color: isTagOnlyEdge ? '#9ca3af' : '#ef4444', width: 18, height: 18 },
        },
    ];

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    useMemo(() => {
        setEdges((eds) =>
            eds.map((edge) => {
                if (edge.id === 'e-close') {
                    return {
                        ...edge,
                        style: { stroke: isTagOnlyEdge ? '#9ca3af' : '#ef4444', strokeWidth: 2, strokeDasharray: isTagOnlyEdge ? '6 4' : undefined },
                        label: isTagOnlyEdge ? 'Manual removal' : 'PR closed / merged',
                        labelStyle: { fontSize: 11, fontWeight: 600, fill: isTagOnlyEdge ? '#9ca3af' : '#dc2626' },
                        markerEnd: { type: MarkerType.ArrowClosed, color: isTagOnlyEdge ? '#9ca3af' : '#ef4444', width: 18, height: 18 },
                    };
                }
                return edge;
            })
        );
        setNodes((nds) =>
            nds.map((node) => {
                const selMap: Record<string, string> = {
                    trigger: 'trigger',
                    createNamespace: 'namespace',
                    deployApps: 'apps',
                    removePreview: 'cleanup',
                };
                const isSelected = selectedNodeId === selMap[node.id];

                if (node.id === 'trigger') {
                    return {
                        ...node,
                        selected: isSelected,
                        data: { ...node.data, enabled: formState.enabled, targetBranch: formState.targetBranch, branchPattern: formState.branchPattern, triggerEvents: formState.triggerEvents, tagPattern: formState.tagPattern },
                    };
                }
                if (node.id === 'createNamespace') {
                    const cluster = clusters?.find(c => c.id === formState.rootClusterId);
                    return {
                        ...node,
                        selected: isSelected,
                        data: { ...node.data, clusterId: formState.rootClusterId, clusterName: cluster?.name, namingPattern: formState.namingPattern, maxConcurrentPreviews: formState.maxConcurrentPreviews },
                    };
                }
                if (node.id === 'deployApps') {
                    return { ...node, selected: isSelected, data: { ...node.data, apps: formState.apps, postCommentToPR: formState.postCommentToPR } };
                }
                if (node.id === 'removePreview') {
                    return { ...node, selected: isSelected, data: { ...node.data, autoDestroyOnPRClose: formState.autoDestroyOnPRClose, triggerEvents: formState.triggerEvents } };
                }
                return node;
            })
        );
    }, [formState, clusters, selectedNodeId, setNodes, setEdges, isTagOnlyEdge]);

    const isPaused = !formState.enabled;

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onPaneClick={() => onNodeSelect(null)}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.15 }}
                minZoom={0.3}
                maxZoom={1.5}
                proOptions={{ hideAttribution: true }}
            >
                <Background variant={BackgroundVariant.Dots} gap={20} size={1.2} color="#d1d5db" />
                <Controls showInteractive={false} />
            </ReactFlow>
            {isPaused && (
                <div style={{
                    position: 'absolute',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    background: 'rgba(55, 65, 81, 0.92)',
                    borderRadius: 10,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    letterSpacing: '-0.01em',
                }}>
                    <Pause size={14} />
                    Workflow paused — configure and activate when ready
                </div>
            )}
        </div>
    );
}
